import Ably from 'ably';
import { endpoints } from '~/services/amplify-api/endpoints';
import { GetOrganizationId, GetUserData } from '@/constants/getters';
import { AddRealtimeListener, ClearRealtimeListener } from '@/constants/mutations';

const CHANNEL_SCOPE = {
  ORGANIZATION: 'organization',
  SYSTEM: 'system',
  USER: 'user',
};

const ABLY_CONNECTION_OPTS = {
  disconnectedRetryTimeout: 100,
  suspendedRetryTimeout: 100,
};

export class RealtimeService {
  init(ctx) {
    const { $amplifyApi, store } = ctx;
    this.store = store;
    this.api = $amplifyApi;

    this.realtime = new Ably.Realtime({
      authCallback: async (_, cb) => {
        try {
          const response = await this.api.get(endpoints.realtime.requestToken);
          cb(null, response?.data);
        } catch (err) {
          console.error(err);
          cb(err, null);
        }
      },
      ...ABLY_CONNECTION_OPTS,
    });
    this.realtime.connection.on('connected', () => {
      console.info('Connected to Ably!'); // successful connection
      this.store.dispatch('chatbotStore/setAblyReady');
    });
    this.realtime.connection.on('failed', () => {
      throw new Error('Failed to connect to Ably!'); // failed connection
    });
  }

  /**
   * Publish a message to the User scoped channel.
   */
  publishToUserChannel({ channel, topic, message }) {
    return this.#publish({ channel, topic, message }, CHANNEL_SCOPE.USER);
  }

  /**
   * Publish a message to the Organization scoped channel.
   */
  publishToOrganizationChannel({ channel, topic, message }) {
    return this.#publish({ channel, topic, message }, CHANNEL_SCOPE.ORGANIZATION);
  }

  /**
   * Publish a message to the System scoped channel.
   */
  publishToSystemChannel({ channel, topic, message }) {
    return this.#publish({ channel, topic, message }, CHANNEL_SCOPE.SYSTEM);
  }

  /**
   * Subscribe to messages scoped to User channel.
   */
  subscribeToUserChannel({ channel, topic, cb }) {
    return this.#subscribe({ channel, topic, cb }, CHANNEL_SCOPE.USER).then((channelInstance) => {
      channelInstance.presence.enter();
    });
  }

  subscribeToPresenceChannel(channel, cb) {
    const channelInstance = this.realtime.channels.get(channel);
    channelInstance.setOptions({ params: { occupancy: 'metrics' } });
    return channelInstance.subscribe('[meta]occupancy', cb);
  }

  /**
   * Subscribe to messages scoped to Organization channel.
   */
  subscribeToOrganizationChannel({ channel, topic, cb }) {
    return this.#subscribe({ channel, topic, cb }, CHANNEL_SCOPE.ORGANIZATION);
  }

  /**
   * Subscribe to messages scoped to System channel.
   */
  subscribeToSystemChannel({ channel, topic, cb }) {
    return this.#subscribe({ channel, topic, cb }, CHANNEL_SCOPE.SYSTEM);
  }

  /**
   * Deregister a message listener scoped to User channel.
   */
  unsubscribeFromUserChannel({ channel, topic, cb }) {
    return this.#unsubscribe({ channel, topic, cb }, CHANNEL_SCOPE.USER).then((channelInstance) => {
      channelInstance.presence.leave();
    });
  }

  /**
   * Deregister a message listener scoped to Organization channel.
   */
  unsubscribeFromOrganizationChannel({ channel, topic, cb }) {
    return this.#unsubscribe({ channel, topic, cb }, CHANNEL_SCOPE.ORGANIZATION);
  }

  /**
   * Deregister a message listener scoped to System channel.
   */
  unsubscribeFromSystemChannel({ channel, topic, cb }) {
    return this.#unsubscribe({ channel, topic, cb }, CHANNEL_SCOPE.SYSTEM);
  }

  /**
   * Open and maintain a connection to Ably.
   */
  async #connect() {
    if (this.realtime.connection.state !== 'connected') {
      await this.realtime.connection.once('connected');
    }
  }

  /**
   * Publishes a single message to the channel with the given event name and payload.
   * If a topic is present, publishes to the specified topic.
   */
  async #publish({ channel, topic, message }, scope) {
    await this.#connect();

    if (!scope) {
      throw new Error('Scope is required');
    }
    const channelInstance = this.#getChannel(channel, scope);
    if (!message) {
      throw new Error('Message is required');
    }
    if (topic) {
      channelInstance.publish(topic, message);
    } else {
      channelInstance.publish(message);
    }
    return channelInstance;
  }

  /**
   * Subscribe to messages on this channel.
   * The caller supplies a listener function, which is called each time one or more messages arrives on the channel.
   * If a topic is present, subscribes to the specified topic.
   */
  async #subscribe({ channel, topic, cb }, scope) {
    await this.#connect();

    if (!scope) {
      throw new Error('Scope is required');
    }
    const channelInstance = this.#getChannel(channel, scope);
    if (!cb) {
      throw new Error('Callback is required');
    }
    // Add callback to store map, for later retrieval when unsubscribing in different context
    this.store.commit(`realtimeStore/${AddRealtimeListener}`, { listener: cb, scope, channel, topic });

    if (topic) {
      channelInstance.subscribe(topic, cb);
    } else {
      channelInstance.subscribe(cb);
    }
    return channelInstance;
  }

  /**
   * Deregisters a specific listener that is registered to receive messages on the channel.
   * If a topic is present, unsubscribes from the specified topic.
   */
  async #unsubscribe({ channel, topic, cb }, scope) {
    await this.#connect();

    if (!scope) {
      throw new Error('Scope is required');
    }
    const channelInstance = this.#getChannel(channel, scope);
    if (topic) {
      if (!cb) {
        throw new Error('Callback is required');
      }
      this.store.commit(`realtimeStore/${ClearRealtimeListener}`, { scope, channel, topic });

      channelInstance.unsubscribe(topic, cb);

      return channelInstance;
    }
    // Not a perfect implementation - we need to be able to loop over all instances containing the channel
    // Additionally, this implementation doesn't take into account multiple listeners added to the same channel (and optionally, same topic),
    // as the store only holds one callback per combination of values
    this.store.commit(`realtimeStore/${ClearRealtimeListener}`, { scope, channel });

    if (cb) {
      channelInstance.unsubscribe(cb);
    } else {
      channelInstance.unsubscribe();
    }
    return channelInstance;
  }

  /**
   * Creates or returns the scoped channel object.
   */
  #getChannel(channel, scope) {
    if (!channel) {
      throw new Error('Channel is required');
    }
    if (!scope) {
      throw new Error('Scope is required');
    }
    switch (scope) {
      case CHANNEL_SCOPE.ORGANIZATION:
        return this.#getOrganizationChannel(channel);
      case CHANNEL_SCOPE.USER:
        return this.#getUserChannel(channel);
      case CHANNEL_SCOPE.SYSTEM:
        return this.#getSystemChannel(channel);
      default:
        throw new Error(`Unsupported channel scope: ${scope}`);
    }
  }

  #getSystemChannel(channel) {
    return this.realtime.channels.get(`system:${channel}`);
  }

  #getOrganizationChannel(channel) {
    const organizationId = this.store.getters[`authStore/${GetOrganizationId}`];

    return this.realtime.channels.get(`${organizationId}:${channel}`);
  }

  #getUserChannel(channel) {
    const user = this.store.getters[`authStore/${GetUserData}`];
    const organizationId = this.store.getters[`authStore/${GetOrganizationId}`];

    return this.realtime.channels.get(`${organizationId}:${user.id}:${channel}`);
  }
}
